/* eslint-disable lingui/no-unlocalized-strings -- nomes de recurso, tipo e ação do fio, não texto de UI */
import { getDefaultPermissionList, type CatalogEntry } from './catalog';
import { PERMISSION_LEVELS, PERMISSION_TYPES, type PermissionLevel, type PermissionType } from './permission-types';
import type { PermissionEntry } from './permission.wire';

/** Uma linha do catálogo preenchida com o que o usuário tem (`PermissionModel` do legado). */
export type PermissionModel = CatalogEntry;

/** `Recurso:Tipo` concede sobre outro recurso; sem separador, sobre o próprio (`parseActionKey`). */
export function parseActionKey(resource: string, actionKey: string): { resource: string; type: string } {
  const at = actionKey.indexOf(':');
  if (at < 0) return { resource, type: actionKey };
  return { resource: actionKey.slice(0, at), type: actionKey.slice(at + 1) };
}

// Nível pelo índice; índice fora da faixa vira `undefined`, como `permissionLevel[level]` no legado.
function normalize(matches: readonly PermissionEntry[]): PermissionLevel | undefined {
  const first = matches[0];
  if (!first) return 'None';
  return PERMISSION_LEVELS[first.level];
}

function levelFor(entries: readonly PermissionEntry[], resource: string, type: string) {
  return normalize(entries.filter((item) => item.resourceDisplay === resource && PERMISSION_TYPES[item.type] === type));
}

/**
 * `fromApiDataModel` do legado: cruza `GET /Permission` com o catálogo. CRUD ausente no catálogo fica
 * ausente; ação só é `true` com nível `All`; recurso fora do catálogo é descartado.
 */
export function fromApiDataModel(entries: readonly PermissionEntry[]): PermissionModel[] {
  return getDefaultPermissionList().map((perm) => {
    const next: PermissionModel = { ...perm };
    const fill = (key: 'readPermission' | 'createPermission' | 'updatePermission' | 'deletePermission', type: string) => {
      if (!perm[key]) return;
      const level = levelFor(entries, perm.resource, type);
      if (level === undefined) Reflect.deleteProperty(next, key);
      else next[key] = level;
    };
    fill('readPermission', 'Read');
    fill('createPermission', 'Create');
    fill('updatePermission', 'Update');
    fill('deletePermission', 'Delete');
    if (perm.actions) {
      next.actions = Object.fromEntries(
        Object.keys(perm.actions).map((action) => {
          const target = parseActionKey(perm.resource, action);
          return [action, levelFor(entries, target.resource, target.type) === 'All'];
        }),
      );
    }
    return next;
  });
}

const matchesResource = (perm: PermissionModel, resource: string | readonly string[]) =>
  perm.resource === resource || (typeof resource !== 'string' && resource.includes(perm.resource));

// `!== 'None'` de propósito: CRUD ausente no catálogo (undefined) conta como concedido, como no legado.
const crudGranted = (perm: PermissionModel, type: PermissionType) =>
  (type === 'Read' && perm.readPermission !== 'None') ||
  (type === 'Create' && perm.createPermission !== 'None') ||
  (type === 'Update' && perm.updatePermission !== 'None') ||
  (type === 'Delete' && perm.deletePermission !== 'None');

const actionGranted = (perm: PermissionModel, type: PermissionType) => perm.actions?.[type] === true;

/**
 * `hasPermission` do legado: CRUD pelo recurso; **ação em qualquer recurso** (não amarra ao pedido).
 * Sem dados carregados, `false`.
 */
export function hasPermission(model: readonly PermissionModel[] | undefined, resource: string | readonly string[], type: PermissionType): boolean {
  if (model === undefined) return false;
  return model.some((perm) => (matchesResource(perm, resource) && crudGranted(perm, type)) || actionGranted(perm, type));
}

/** `hasScopedPermission` do legado: igual, mas a ação também precisa estar no recurso pedido (`strictPermission`). */
export function hasScopedPermission(
  model: readonly PermissionModel[] | undefined,
  resource: string | readonly string[],
  type: PermissionType,
): boolean {
  if (model === undefined) return false;
  return model.some((perm) => matchesResource(perm, resource) && (crudGranted(perm, type) || actionGranted(perm, type)));
}

/** `checkActionsPerms` do legado: primeira ação do recurso cujo nome contém `actions`. */
export function checkActionsPerms(actions: string, model: readonly PermissionModel[] | undefined, resource: string): boolean {
  const perms = model?.find((perm) => perm.resource === resource)?.actions ?? {};
  const first = Object.keys(perms).find((key) => key.includes(actions));
  return first !== undefined && perms[first] === true;
}
